import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, Renderer2, TemplateRef, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, concat, from, of } from 'rxjs';
import { distinctUntilChanged, first, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { GetConnextDataQuery, isNotNullOrUndefined, ProductModelService, RoleService, System, wait } from 'ngx-core-services';
import { PageEvent } from '@angular/material/paginator';
import { ColorTypeEnum, ColumnConfig, ColumnTypeEnum, UiPromiseService, NO_LEN } from 'ngx-zcs-components';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { FormBuilder } from '@angular/forms';

import { CreateCustomBlockSizeType, ICustomBlock } from '../../decorators';
import { SystemsState } from '../../../store';
import { PortingService } from '../porting.service';
import { connextDataQueryForPorting } from '../../block-state/query-constructor.query';
import { SystemPromotionStatusMap, PortabilityLoadOptions, Portability } from './base-systems-porting.models';
import { SystemPromotionPaginator } from './system-promotion-paginator.utils';
import { getLabels, isListUpdated, trackByFn } from './base-systems-porting.utils';
import { SystemsPortingReportDialogComponent } from './report-dialog/porting-report-dialog';

@CreateCustomBlockSizeType({})
@Component({
  selector: 'lib-base-systems-porting',
  templateUrl: './base-systems-porting.component.html',
  styleUrls: ['./base-systems-porting.component.scss'],
})
export class BaseSystemsPortingComponent implements ICustomBlock, AfterViewInit {
  @ViewChild('plantNamePreview', { static: true }) plantNamePreview!: TemplateRef<any>;
  @ViewChild('actionPreview', { static: true }) actionPreview!: TemplateRef<any>;
  @ViewChild('devicesPreview', { static: true }) devicesPreview!: TemplateRef<any>;
  @ViewChild('actionHeaderPreview', { static: true }) actionHeaderPreview!: TemplateRef<any>;

  readonly _systems$ = new BehaviorSubject<System[] | null>(null);
  @Input() set systems(value: System[] | null | undefined) {
    if (value && isListUpdated(value, this._systems$.value)) {
      this._systems$.next(value);
    }
  }
  get systems(): System[] | null {
    return this._systems$.value;
  }

  readonly _labels = getLabels();
  @Input() set labels(value: typeof this._labels) {
    Object.assign(this._labels, value);
    this.updateColumns();
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly query$ = new BehaviorSubject<string>('');
  @Input() set query(value: string) {
    this.query$.next(value);
    this.pagination$.next({ pageIndex: 0, pageSize: this.pagination$.value.pageSize });
  }
  get query(): string {
    return this.query$.value;
  }

  @Input() portingDisclaimerTemplate?: TemplateRef<any>;

  readonly loading$ = new BehaviorSubject<boolean>(false);

  readonly filters = this.formBuilder.group({
    onlyPortable: true,
  });

  readonly pagination$ = new BehaviorSubject<{ pageIndex: number; pageSize: number; hasMore?: boolean }>({
    pageIndex: 0,
    pageSize: 10,
  });

  columns: ColumnConfig[] = [];

  readonly dataSource$ = combineLatest([
    this._systems$.pipe(isNotNullOrUndefined()),
    this.query$.pipe(
      distinctUntilChanged(),
      tap((_) => this.pagination$.next({ pageIndex: 0, pageSize: this.pagination$.value.pageSize })),
    ),
    this.filters.valueChanges.pipe(
      distinctUntilChanged((a, b) => a.onlyPortable === b.onlyPortable),
      tap((_) => this.pagination$.next({ pageIndex: 0, pageSize: this.pagination$.value.pageSize })),
      startWith(this.filters.value),
    ),
    this.pagination$.pipe(distinctUntilChanged((a, b) => a.pageIndex === b.pageIndex && a.pageSize === b.pageSize))
  ]).pipe(
    switchMap(([systems, query, filters, pagination]) => {
      if (!this.portabilityPaginator) {
        return of([]);
      }

      if (!this.portabilityPaginator.state.isUpdated) {
        this.portabilityPaginator.setSystemsData(systems);
      }

      const filterBaseSystems = (baseSystems: System[]): System[] => {
        const lowerQuery = query.trim().toLowerCase();
        if (!lowerQuery) {
          return baseSystems;
        }
        return baseSystems.filter(system =>
          system.name.toLowerCase().includes(lowerQuery) ||
          (system.connextSn && system.connextSn.toLowerCase().includes(lowerQuery)) ||
          (system.configuration &&
            system.configuration.connextId &&
            system.configuration.connextId.toLowerCase().includes(lowerQuery)) ||
          system.devices.some(device => device.id.toLowerCase().includes(lowerQuery))
        );
      };

      const filterCandidateSystems = (candidateSystems: SystemPromotionStatusMap) => {
        if (!filters.onlyPortable) {
          return candidateSystems;
        }

        return Object.entries(candidateSystems).filter(([_, candidateSystem]) => candidateSystem.isPromotable).reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {} as SystemPromotionStatusMap);
      };

      const loadOptions: PortabilityLoadOptions = {
        pageSize: pagination.pageSize,
        currentPage: pagination.pageIndex + 1,
        filterBaseSystems,
        filterCandidateSystems,
        hasMore: (hasMore: boolean) => this.pagination$.next({ ...pagination, hasMore }),
      };

      return concat(
        of(null),
        from(this.portabilityPaginator.loadPage(loadOptions))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly portingSelectedPlants$ = this.dataSource$.pipe(
    switchMap((systems) =>
      systems?.length ? combineLatest(systems.map((s) => s.selected$)).pipe(
        map((choices) => choices.filter((e) => e))
      ) : of([]),
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly portingSelectAll$ = combineLatest([
    this.dataSource$,
    this.portingSelectedPlants$,
  ]).pipe(
    map(([systems, selectedPlants]) => selectedPlants.length === systems?.length ? 'checked' : 'unchecked'),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly isPortingDisabled$ = this.portingSelectedPlants$.pipe(
    map((choices) => choices.length === 0),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly displayedColumns = ['name', 'devices', 'portability', 'action'];
  readonly NO_LEN = NO_LEN;
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly Portability = Portability;
  readonly portabilityPaginator;
  readonly trackByFn = trackByFn;
  readonly userAuthorized$ = this.roleService.has_somePermission$;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly formBuilder: FormBuilder,
    private readonly getConnextDataQuery: GetConnextDataQuery,
    private readonly portingService: PortingService,
    private readonly roleService: RoleService,
    private readonly uiPromiseService: UiPromiseService,
    readonly el: ElementRef,
    readonly renderer: Renderer2,
    readonly store: Store<SystemsState>,
  ) {
    this.portabilityPaginator = new SystemPromotionPaginator({
      advancedSource: 'aws',
      defaultPageSize: 10,
      loading$: this.loading$,
      getDevicesPlantIds: async (deviceIds: string[]) => {
        const lowerCaseIds = deviceIds.map(id => id.toLocaleLowerCase());
        const results = await this.portingService.getDevicesIsAlreadyInPlants(lowerCaseIds);

        return deviceIds.reduce((acc, id) => {
          const device = results[id.toLocaleLowerCase()] ?? results[id.toUpperCase()];
          acc[id] = device.plantId;
          return acc;
        }, {} as { [deviceId: string]: string | null | undefined });
      },
      getConnextDeviceAndUserLevel: async (connextId: string) => {
        const connextData = await this.getConnextDataQuery.execute(
          connextDataQueryForPorting,
          { id: connextId },
          true
        );
        const devices = connextData?.data?.getConnextData?.devices
          ?.filter(d => d.sn)
          .map(device => device.sn.toLocaleLowerCase())
          .filter(d => {
            const product = new ProductModelService(d);
            return product.type?.name === 'inverter';
          })
          .concat(connextId);
        const userLevel = connextData?.data?.getConnextData?.userLevel;
        return { devices, userLevel };
      },
    });

    this.portPlants = this.portPlants.bind(this);
  }

  ngAfterViewInit(): void {
    this.updateColumns();
  }

  toggleAllSelection(event: { checked: boolean }) {
    const checked = event.checked;
    this.dataSource$.pipe(first()).subscribe((systems) => {
      systems?.forEach((s) => {
        if (s.portability === Portability.PORTABLE) {
          s.selected$.next(checked);
        }
      });
    });
  }

  onPageChange(event: PageEvent): void {
    this.pagination$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  async portPlants() {
    const plantPorted: string[] = [];
    const plantFailed: string[] = [];
    await this.uiPromiseService.run(async () => {
      const systems = await this.dataSource$.pipe(first()).toPromise();
      if (!systems) {
        return;
      }

      const selectedSystems = systems.filter((system) => system.selected$.value);
      if (selectedSystems.length === 0) {
        return;
      }

      await Promise.all(selectedSystems.map(async ({ system }, index) => {
        try {
          await wait(index * 10);
          const result = await this.portingService.portPlantToAws(system);
          if (result) {
            plantPorted.push(system.name);
          }
        } catch {
          plantFailed.push(system.name);
        }
      }));

      const WAIT_TO_TRIGGER_SUBSCRIPTION = 5 * 1000;
      await wait(WAIT_TO_TRIGGER_SUBSCRIPTION);
      this.toggleAllSelection({ checked: false });
    },{
      loading: true,
    });

    this.changeDetectorRef.detectChanges();

    const portingResults = this.dialog.open(SystemsPortingReportDialogComponent, {
      width: plantFailed.length && plantPorted.length ? '800px' : '600px',
      data: {
        plantPorted,
        plantFailed,
        label: this.labels,
      },
    });

    portingResults.afterClosed().subscribe(() => {
      const systems = this._systems$.value;
      if (!systems) {
        return;
      }

      this.portabilityPaginator.setSystemsData(systems);
      this._systems$.next(systems);
    });
  }

  updateColumns() {
    this.columns = ([
      {
        columnDef: 'portabilityName',
        type: ColumnTypeEnum.Template,
        header: this.labels.systemName,
        template: this.plantNamePreview,
        sortable: false,
      },
      {
        columnDef: 'portabilityDevicesPreview',
        header: this.labels.systemDevices,
        type: ColumnTypeEnum.Template,
        template: this.devicesPreview,
        sortable: false,
      },
      {
        columnDef: 'portabilityStatus',
        header: this.labels.systemStatus,
        type: ColumnTypeEnum.Chip,
        cell: (row: {
            system: System;
            selected$: BehaviorSubject<boolean>;
            portability: Portability;
            toggleSelection(): void;
        }) => {
          return row.portability === Portability.NOT_PORTABLE ? this.labels.notPortable :
            row.portability === Portability.FULLY_PORTED ? this.labels.alreadyPortedWithAllDevices :
            row.portability === Portability.ALREADY_PORTED_WITH_SOME_DEVICES ? this.labels.alreadyPortedWithSomeDevices :
            row.portability === Portability.ALREADY_PORTED_WITH_ALL_DEVICES ? this.labels.allPortedContained :
            row.portability === Portability.PORTABLE ? this.labels.portable :
            this.labels.notPortable;
        },
        chipColor: (row: {
          system: System;
          selected$: BehaviorSubject<boolean>;
          portability: Portability;
          toggleSelection(): void;
        }) => {
          return row.portability === Portability.NOT_PORTABLE ? ColorTypeEnum.Red :
            row.portability === Portability.FULLY_PORTED ? ColorTypeEnum.Green :
            row.portability === Portability.ALREADY_PORTED_WITH_SOME_DEVICES ? ColorTypeEnum.Yellow :
            row.portability === Portability.ALREADY_PORTED_WITH_ALL_DEVICES ? ColorTypeEnum.Green :
            row.portability === Portability.PORTABLE ? ColorTypeEnum.Primary :
            ColorTypeEnum.Red;
        },
        sortable: false,
      },
      {
        columnDef: 'portabilityActionPreview',
        header: this.labels.action,
        headerTemplate: this.actionHeaderPreview,
        type: ColumnTypeEnum.Template,
        template: this.actionPreview,
        sortable: false,
      },
    ])
  }
}
